import {
    FC,
    getProp,
    PointLike,
    copyPointData,
    CanBeSignals,
    JSX,
} from '@gongxigames/framework/core/fragments/BuilderY';
import {
    FullScreenCover as FullScreenCoverConstructor,
    FullScreenCoverOptions,
    FullScreenCoverUpdateOptions
} from '@gongxigames/framework/core/fragments/display/SafeZone/FullScreenCover';
import {Container as PixiContainer, Point} from 'pixi.js';
import {Store} from '@gongxigames/framework/core/fragments/store';
import {slotStore, strictUiEmitter} from '@gongxigames/framework/slotV2/globals';
import {SafeZone} from '@gongxigames/framework/core/fragments/display/SafeZone/SafeZone';

export type FullScreenCoverProps = CanBeSignals<{
    width: number;
    height: number;
    align?: PointLike;
    offset?: PointLike;
    safeZone?: SafeZone;
    children: JSX.Element;
}>;

function updateEffect<T extends FullScreenCoverConstructor>(props: FullScreenCoverProps, instance: T): () => any {
    const {width, height, align, offset, safeZone, children} = props;

    return Store.effect(() => {
        const alignVal = getProp(align);
        const offsetVal = getProp(offset);
        const safeZoneVal = getProp(safeZone) ?? slotStore.stage_safeZoneState_game.value;
        const container = getProp(children);

        if (!(container instanceof PixiContainer)) {
            throw new Error('FullScreenCover: shod contain only one child element');
        }

        const options: FullScreenCoverUpdateOptions<PixiContainer> = {
            container,
            safeZone: safeZoneVal,
            contentSize: {
                width: getProp(width),
                height: getProp(height)
            },
        };

        if (alignVal !== undefined) {
            options.align ??= new Point();
            copyPointData(alignVal, options.align);
        }

        if (offsetVal !== undefined) {
            options.offset ??= new Point();
            copyPointData(offsetVal, options.offset);
        }

        instance.update(options);
    });
}

function createCover(props: FullScreenCoverProps): FullScreenCoverConstructor {
    const container = getProp(props.children);
    const safeZone = getProp(props.safeZone) ?? slotStore.stage_safeZoneState_game.value;
    const align = getProp(props.align);
    const offset = getProp(props.offset);

    if (!(container instanceof PixiContainer)) {
        throw new Error('FullScreenCover: shod contain only one child element');
    }

    const options: FullScreenCoverOptions = {
        container,
        contentSize: {
            width: getProp(props.width),
            height: getProp(props.height),
        },
        safeZone,
    };

    if (align !== undefined) {
        options.align = new Point();
        copyPointData(align, options.align);
    }

    if (offset !== undefined) {
        options.offset = new Point();
        copyPointData(offset, options.offset);
    }

    return new FullScreenCoverConstructor(options);
}

export const $FullScreenCover: FC<FullScreenCoverProps> = (props) => {
    const fullScreenCover = createCover(props);
    const offUpdate = updateEffect(props, fullScreenCover);
    const offResize = strictUiEmitter.on('CORE.screen.resize', () => {
        const safeZone = getProp(props.safeZone) ?? slotStore.stage_safeZoneState_game.value;

        fullScreenCover.resize(safeZone);
    });

    fullScreenCover.container.on('destroyed', offUpdate);
    fullScreenCover.container.on('destroyed', offResize);

    return fullScreenCover.container;
};